import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@app/auth/AuthContext';
import { featureFlags } from '@admin/shared/featureFlags';
import {
  getSubscription,
  canAccessChordAi,
  canAccessApiKeys,
  type Subscription,
} from '@app/api/subscription';

// ----------------------------------------------------------------------------
// EntitlementsContext — single source of truth for subscription-derived feature
// access (Chord AI, API keys). Previously the navbar and the Chord AI route guard
// each fetched the subscription in their own effect keyed on churchId, so a tier
// change (e.g. cancel/downgrade) on the subscription page did not update them
// until a full page reload. Components now read from here, and any action that
// changes the tier calls refreshEntitlements() to update everyone at once.
// ----------------------------------------------------------------------------

interface EntitlementsContextType {
  subscription: Subscription | null;
  canUseChordAi: boolean;
  canUseApiKeys: boolean;
  isLoading: boolean;
  refreshEntitlements: () => Promise<void>;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

export const EntitlementsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEntitlements = useCallback(async () => {
    if (!user?.churchId) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await getSubscription(user.churchId);
      setSubscription(data);
    } catch {
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.churchId]);

  useEffect(() => {
    void fetchEntitlements();
  }, [fetchEntitlements]);

  const canUseChordAi = featureFlags.chordAi && canAccessChordAi(subscription);
  const canUseApiKeys = canAccessApiKeys(subscription);

  return (
    <EntitlementsContext.Provider
      value={{
        subscription,
        canUseChordAi,
        canUseApiKeys,
        isLoading,
        refreshEntitlements: fetchEntitlements,
      }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = (): EntitlementsContextType => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
};
